import { grey } from '@mui/material/colors';
import Student from '../../../model/Student';
import { Checkbox, TextField, Typography } from '@mui/material';
import CustomTable from '../CustomTable';
import { useContext, useEffect, useState } from 'react';
import { getSecondBStageStudents, updateSecondBStageResult } from '../../../api/content-api';
import { loginPage } from '../../../store/fixed-routing';
import AuthContext from '../../../store/auth-context';
import SnackbarContext from '../../../store/snackbar-context';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../ui/Loader';
import ActionButtons from './ActionButtons';
import Result from '../../../model/Result';
import OlympicsContext from '../../../store/olympics-context';
import { DEFAULT_API_ERROR, DEFAULT_AUTH_ERROR } from '../../../utils/auth-tools';
import { filter } from '../StagesTable';

const SecondBStageTable = () => {
	const [allStudents, setAllStudents] = useState([]);
	const [displayedStudents, setDisplayedStudents] = useState<Student[]>([]);
	const [editedStudent, setEditedStudent] = useState<Student>();
	const [isLoading, setIsLoading] = useState(true);
	const [isUpdating, setIsUpdating] = useState(false);
	const { logout, token, isSecretary, isAdmin, id } = useContext(AuthContext);
	const { olympics, refresh, districtFilterId } = useContext(OlympicsContext);
	const { setMsg } = useContext(SnackbarContext);
	const navigate = useNavigate();
	const editable =
		olympics && (isAdmin || !(olympics?.secondStagePoints?.partB?.stageClosed || olympics?.secondBStagePublished));

	useEffect(() => {
		setDisplayedStudents(filter(allStudents, districtFilterId, id));
	}, [districtFilterId]);

	const getSecondBStagePoints = (s: Student) => {
		return s.result.secondBOneStagePoints + s.result.secondBTwoStagePoints + s.result.secondBThreeStagePoints;
	};

	const getSecondStageTotalPoints = (s: Student) => {
		return getSecondBStagePoints(s) + s.result.secondAOneStagePoints + s.result.secondATwoStagePoints;
	};

	const downloadData = () => {
		getSecondBStageStudents(token)
			.then((response) => {
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setAllStudents(students);
					setDisplayedStudents(filter(students, districtFilterId, id));
				} else {
					console.log(response);
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: DEFAULT_AUTH_ERROR });
				} else {
					const msg = exception?.response?.data?.message ? exception?.response?.data?.message : DEFAULT_API_ERROR;
					setMsg({ msg });
				}
				setIsLoading(false);
			});
	};

	const handleSaveFirstStageResult = () => {
		setIsUpdating(true);
		const data = {
			id: editedStudent?.id,
			secondBOneStagePoints: editedStudent?.result.secondBOneStagePoints,
			secondBTwoStagePoints: editedStudent?.result.secondBTwoStagePoints,
			secondBThreeStagePoints: editedStudent?.result.secondBThreeStagePoints,
			secondBStageQualified: isAdmin ? editedStudent?.result.secondBStageQualified : undefined,
		};
		updateSecondBStageResult(token, data)
			.then((response) => {
				setIsUpdating(false);
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setAllStudents(students);
					setDisplayedStudents(filter(students, districtFilterId, id));
					setEditedStudent(undefined);
					setMsg({ msg: 'Dane ucznia zostały zaktualizowane', severity: 'success' });
				} else {
					console.log(response);
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
			})
			.catch((exception) => {
				setIsUpdating(false);
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: DEFAULT_AUTH_ERROR });
				} else {
					const msg = exception?.response?.data?.message ? exception?.response?.data?.message : DEFAULT_API_ERROR;
					setMsg({ msg });
				}
				setIsLoading(false);
			});
	};

	useEffect(() => {
		refresh();
		downloadData();
	}, []);

	const handlePointsChange = (event: any, accessor: keyof Result) => {
		if (editedStudent) {
			const result = { ...editedStudent.result };
			// @ts-ignore
			result[accessor] = Number(event.target.value);
			setEditedStudent({
				...editedStudent,
				result: result,
			});
		}
	};

	const renderPointsField = (i: Student, accessor: keyof Result) => {
		return editedStudent?.id === i.id ? (
			<TextField
				variant={'standard'}
				type={'number'}
				value={editedStudent.result[accessor]}
				disabled={!editable}
				sx={{
					width: '100%',
					height: '100%',
					justifyContent: 'end',
					'& input': {
						pl: '7%',
						minHeight: '100%',
						margin: 0,
						fontSize: '16px',
						textAlign: 'center',
					},
				}}
				onChange={(event) => handlePointsChange(event, accessor)}
			/>
		) : (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{i.result[accessor]}
			</Typography>
		);
	};

	const renderSumField = (i: Student) => {
		let value = 0;
		if (editedStudent?.id === i.id) {
			value = getSecondBStagePoints(editedStudent);
		} else {
			value = getSecondBStagePoints(i);
		}
		value = isNaN(value) ? 0 : value;
		return (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{value}
			</Typography>
		);
	};

	const renderTotalSumField = (i: Student) => {
		let value = 0;
		if (editedStudent?.id === i.id) {
			value = getSecondStageTotalPoints(editedStudent);
		} else {
			value = i.result.secondBStageTotalPoints;
		}
		value = isNaN(value) ? 0 : value;
		return (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{value}
			</Typography>
		);
	};

	const handleQualifiedChange = () => {
		if (editedStudent) {
			setEditedStudent({
				...editedStudent,
				result: { ...editedStudent.result, secondBStageQualified: !editedStudent.result.secondBStageQualified },
			});
		}
	};

	const renderQualifiedField = (i: Student) => {
		const value =
			editedStudent?.id === i.id && isAdmin
				? editedStudent.result.secondBStageQualified
				: i.result.secondBStageQualified;
		return (
			<Checkbox
				checked={value}
				disabled={editedStudent?.id !== i.id || !isAdmin}
				onChange={handleQualifiedChange}
			/>
		);
	};

	const data = (input: any) => {
		let additionalColumns: any[] = [];

		if (isAdmin) {
			additionalColumns = [
				{ header: 'Imię', accessor: 'firstName', align: 'left' },
				{ header: 'Nazwisko', accessor: 'lastName', align: 'left' },
			];
		}

		return {
			columns: [
				{ header: 'Lp.', accessor: 'index', align: 'left', width: '60px' },
				{ header: 'Tytuł', accessor: 'personalTitle', align: 'left', width: '100px' },
				...additionalColumns,
				{ header: 'Szkoła', accessor: 'school', align: 'left', bgColor: grey[100] },
				{ header: 'Klasa', accessor: 'schoolClass', width: '100px', align: 'center' },
				{ header: 'Specjalizacja', accessor: 'specialization', width: '100px', align: 'center' },
				{ header: 'Punkty I', accessor: 'points1', width: '100px', align: 'center', editable: editable },
				{ header: 'Punkty II', accessor: 'points2', width: '100px', align: 'center', editable: editable },
				{ header: 'Punkty III', accessor: 'points3', width: '100px', align: 'center', editable: editable },
				{ header: 'Suma', accessor: 'sum', width: '100px', align: 'center' },
				{ header: 'Łącznie Etap II', accessor: 'totalSum', width: '100px', align: 'center' },
				{ header: 'Zakwalifikowany', accessor: 'qualified', width: '150px', align: 'center', editable: isAdmin },
				{ header: '', accessor: 'action', align: 'center', width: '150px', nosort: true, editable: editable },
			],

			rows: input
				? input.map((i: Student, index: number) => {
						return {
							index: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{index + 1}
								</Typography>
							),
							personalTitle: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.personalTitle}
								</Typography>
							),
							firstName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.firstName}
								</Typography>
							),
							lastName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.lastName}
								</Typography>
							),
							school: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.school.name}, {i.school.city}
								</Typography>
							),
							schoolClass: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.schoolClass}
								</Typography>
							),
							specialization: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.specialization.name}
								</Typography>
							),
							points1: renderPointsField(i, 'secondBOneStagePoints'),
							points2: renderPointsField(i, 'secondBTwoStagePoints'),
							points3: renderPointsField(i, 'secondBThreeStagePoints'),
							sum: renderSumField(i),
							totalSum: renderTotalSumField(i),
							qualified: renderQualifiedField(i),
							action: (
								<ActionButtons
									student={i}
									disabled={!editable}
									editedStudent={editedStudent}
									inProgress={isUpdating}
									handleSave={handleSaveFirstStageResult}
									handleClose={() => setEditedStudent(undefined)}
									handleEdit={() => setEditedStudent(i)}
								/>
							),
						};
					})
				: [],
		};
	};
	return <>{isLoading ? <Loader /> : <CustomTable data={data(displayedStudents)} />}</>;
};

export default SecondBStageTable;
